/**
 * Job Description management page.
 * Accessible at /ats/jobs/:jobId/job-descriptions
 *
 * Shows all JDs for a specific job, allows uploading new JDs
 * (file or raw text) and viewing parsed results.
 */

import { useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  FileText,
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  Eye,
  ArrowLeft,
  RefreshCw,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DataTable,
  useAuth,
} from '@aaas/shared';
import type { DataTableColumn, DataTableAction } from '@aaas/shared';
import { useJDListQuery } from '../../hooks/useJobDescriptionQueries';
import { useATSRole } from '../../hooks/useATSRole';
import { JDUploadDialog } from '../../components/JDUploadDialog';
import jobDescriptionService from '../../services/job-description.service';
import type { JobDescription, JDProcessingStatus } from '../../types';
import { JD_PROCESSING_STATUS_CONFIG } from '../../types';

// Processing status badge
function ProcessingStatusBadge({ status }: { status: JDProcessingStatus }) {
  const { t } = useTranslation('ats');
  const config = JD_PROCESSING_STATUS_CONFIG[status] ?? { label: status, color: 'gray' as const };

  const colorClasses = {
    yellow: 'bg-yellow-100 text-yellow-700',
    blue: 'bg-blue-100 text-blue-700',
    green: 'bg-green-100 text-green-700',
    red: 'bg-red-100 text-red-700',
    gray: 'bg-gray-100 text-gray-700',
  };

  const dotClasses = {
    yellow: 'bg-yellow-500',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    gray: 'bg-gray-500',
  };

  const color = config.color as keyof typeof colorClasses;
  const label = t(`jd.status.${status}`, config.label);

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[color]}`}>
      {status === 'processing' ? (
        <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
      ) : (
        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${dotClasses[color]}`} />
      )}
      {label}
    </span>
  );
}

// Source badge
function SourceBadge({ jd }: { jd: JobDescription }) {
  const { t } = useTranslation('ats');
  if (jd.file_url) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <FileText className="h-3 w-3 flex-shrink-0" />
        <span className="truncate max-w-[140px]" title={jd.file_name ?? undefined}>
          {jd.file_name ?? t('jd.source.fallback')}
        </span>
        {jd.file_type && (
          <span className="px-1 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600 uppercase flex-shrink-0">
            {jd.file_type}
          </span>
        )}
      </span>
    );
  }
  if (jd.raw_content) {
    return (
      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
        <FileText className="h-3 w-3" />
        {t('jd.source.text')}
      </span>
    );
  }
  return <span className="text-gray-400 text-xs">{t('jd.source.none')}</span>;
}

export function JobDescriptionPage() {
  const { t } = useTranslation('ats');
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user, currentWorkspace, idToken } = useAuth();
  const { isLeaderOrAbove } = useATSRole();

  // Upload dialog
  const [showUploadDialog, setShowUploadDialog] = useState(false);

  // Delete dialog
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingJD, setDeletingJD] = useState<JobDescription | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());

  const {
    data: jdResponse,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useJDListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    jobId,
    enabled: !!user && !!currentWorkspace && !!jobId,
    // Auto-refetch while any JDs are processing
    refetchInterval: 5000,
  });

  const jds = jdResponse?.data?.items ?? [];

  const handleViewDetail = useCallback((jd: JobDescription) => {
    navigate(`/ats/job-descriptions/${jd.id}`);
  }, [navigate, jobId]);

  const handleDeleteClick = useCallback((jd: JobDescription) => {
    setDeletingJD(jd);
    setDeleteError(null);
    setShowDeleteDialog(true);
  }, []);

  const handleDeleteConfirm = async () => {
    if (!deletingJD || !user || !currentWorkspace || !jobId) return;

    setIsDeleting(true);
    setDeleteError(null);
    setDeletingIds((prev) => new Set(prev).add(deletingJD.id));

    try {
      const token = await user.getIdToken();
      await jobDescriptionService.deleteJobDescription(
        currentWorkspace.id,
        token,
        jobId,
        deletingJD.id
      );
      await refetch();
      setShowDeleteDialog(false);
      setDeletingJD(null);
    } catch (err: unknown) {
      let message = t('config.unknownError');
      if (axios.isAxiosError(err) && err.response?.data?.detail) {
        message = err.response.data.detail;
      } else if (err instanceof Error) {
        message = err.message;
      }
      setDeleteError(message);
    } finally {
      setIsDeleting(false);
      setDeletingIds((prev) => {
        const next = new Set(prev);
        if (deletingJD) next.delete(deletingJD.id);
        return next;
      });
    }
  };

  const columns = useMemo<DataTableColumn<JobDescription>[]>(
    () => [
      {
        key: 'name',
        title: t('jd.columns.name'),
        render: (value: string, record: JobDescription) => (
          <button
            onClick={() => handleViewDetail(record)}
            className="font-medium text-orange-600 hover:text-orange-700 hover:underline text-left"
          >
            {value}
          </button>
        ),
      },
      {
        key: 'file_url',
        title: t('jd.columns.source'),
        width: 200,
        render: (_: unknown, record: JobDescription) => <SourceBadge jd={record} />,
      },
      {
        key: 'processing_status',
        title: t('jd.columns.aiStatus'),
        width: 130,
        render: (value: JDProcessingStatus) => <ProcessingStatusBadge status={value} />,
      },
      {
        key: 'updated_at',
        title: t('jd.columns.updated'),
        width: 110,
        sortable: true,
        render: (value: string) => new Date(value).toLocaleDateString(),
      },
      {
        key: 'created_at',
        title: t('jd.columns.created'),
        width: 110,
        sortable: true,
        render: (value: string) => new Date(value).toLocaleDateString(),
      },
    ],
    [handleViewDetail, t]
  );

  const actions = useMemo<DataTableAction<JobDescription>[]>(() => {
    const base: DataTableAction<JobDescription>[] = [
      {
        key: 'view',
        label: t('jd.viewDetail'),
        icon: <Eye className="h-4 w-4" />,
        onClick: handleViewDetail,
        inline: true,
      },
    ];

    if (isLeaderOrAbove) {
      base.push({
        key: 'delete',
        label: t('jd.delete'),
        icon: <Trash2 className="h-4 w-4" />,
        loadingIcon: <Loader2 className="h-4 w-4 animate-spin" />,
        onClick: handleDeleteClick,
        loading: (record) => deletingIds.has(record.id),
        destructive: true,
        inline: true,
      });
    }

    return base;
  }, [isLeaderOrAbove, handleViewDetail, handleDeleteClick, deletingIds, t]);

  if (isLoading && !jdResponse) {
    return (
      <div className="w-full p-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/ats/jobs/${jobId}`)}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('jd.backToJob')}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <FileText className="h-8 w-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('jd.pageTitle')}</h1>
            <p className="text-sm text-gray-500">{t('jd.pageDescription')}</p>
          </div>
          {isFetching && !isLoading && (
            <Loader2 className="h-5 w-5 text-orange-600 animate-spin" />
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            aria-label={t('jd.refresh')}
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          {isLeaderOrAbove && (
            <button
              onClick={() => setShowUploadDialog(true)}
              className="inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {t('jd.uploadJd')}
            </button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            {error instanceof Error ? error.message : t('jd.loadFailed')}
          </AlertDescription>
        </Alert>
      )}

      {/* Table */}
      <DataTable
        columns={columns}
        data={jds}
        loading={isLoading && !jdResponse}
        actions={actions}
        rowKey={(record) => record.id}
        emptyMessage={t('jd.empty')}
      />

      {/* Upload dialog */}
      {jobId && (
        <JDUploadDialog
          open={showUploadDialog}
          onClose={() => setShowUploadDialog(false)}
          jobId={jobId}
          onSuccess={() => refetch()}
        />
      )}

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => { if (!isDeleting) { setShowDeleteDialog(open); if (!open) setDeletingJD(null); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('jd.deleteDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('jd.deleteDialog.body', { name: deletingJD?.name ?? '' })}
            </AlertDialogDescription>
            {deleteError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{deleteError}</span>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('jd.deleteDialog.cancel')}</AlertDialogCancel>
            <Button
              onClick={(e) => { e.preventDefault(); handleDeleteConfirm(); }}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {t('jd.deleteDialog.deleting')}
                </>
              ) : (
                t('jd.deleteDialog.confirm')
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
